import { BareFetcher, SWRResponse } from 'swr';
import useSWRImmutable from 'swr/immutable';

import {
  SkillDiscoveryItem as Item,
  SkillDiscoveryResponse as Response,
} from '@gmm/sdk-voodoo/skillDiscovery';
import { isFolderType, isSkillType } from '@gmm/ui';
import { ReadOnlyOptions } from '~/lib/sdk';
import { ApiSdkSwrKey, swrKeyFor, voodooSdkFetcher } from '~/lib/swr';
import { useSelectedGrade } from '~/work/skillDiscovery/useGrades';
import { useEffect } from 'react';
import { useExerciseLastUpdatedStore } from '../../stores';
import { AxiosError } from 'axios';
import { skillDiscoveryErrorStore } from '../errorStore';

export const getChildrenValueKey = (
  item: Item,
  selectedGrade: string | null,
): ApiSdkSwrKey<'skillDiscovery.show'> => {
  const [type]: ['EXERCISE' | 'FOLDER' | 'TAG'] =
    selectedGrade === 'colleagues'
      ? [isFolderType(item) ? 'FOLDER' : 'EXERCISE']
      : ['TAG'];

  return swrKeyFor(['skillDiscovery.show', item.id, type, item]);
};

export type UseSkillDiscoveryChildren = SWRResponse<Response, AxiosError>;

interface Options extends ReadOnlyOptions {
  item: Item | null;
}

export const useSkillDiscoveryChildren = ({
  item = null,
  readOnly,
}: Options): UseSkillDiscoveryChildren => {
  const setError = skillDiscoveryErrorStore(state => state.setError);
  const [selectedGrade] = useSelectedGrade();
  const setLastUpdated = useExerciseLastUpdatedStore(
    state => state.setLastUpdated,
  );
  const swrKey =
    item && !isSkillType(item)
      ? getChildrenValueKey(item, selectedGrade)
      : null;

  const swrResponse = useSWRImmutable<Response>(swrKey, {
    fetcher: voodooSdkFetcher as BareFetcher<Response>,
    ...(readOnly ? { revalidateOnMount: false } : {}),
  });

  useEffect(() => {
    if (swrResponse.data) {
      setLastUpdated(swrResponse.data);
    }
  }, [swrResponse.data]);

  if (swrResponse.error) {
    setError(swrResponse.error);
  }

  return swrResponse;
};
